// @flow
import * as React from 'react';
import classNames from 'classnames/bind';
import { useMutation } from '@apollo/client';
import { isBefore, parseISO } from 'date-fns';
import { pathOr } from 'ramda';

import { ActivateShowcase } from '../../../shared-queries/ActivateShowcase.graphql';
import settings from '../../../settings';
import FlagIcon from '../../../basic-components/flag-icon';
import { useApolloLightbox } from '../../../hooks/use-apollo-lightbox';
import OnboardingHighlight from '../../../basic-components/onborading-highlight';
import { useOnboardingTour } from '../../../hooks/use-onboarding-tour';
import { selectedShowcaseVar } from '../../../utils/apollo-cache';
import keys from '../../../settings/storage';
import usePersistState from '../../../hooks/use-persist-state';

import AuFreePopup from './au-free-popup';
import Tooltip from '../tooltip';
import t from './locale';
import cs from './styles.pcss';

import type {
  StockData_showcases_site as SiteT,
  UserLimits_viewer_limits as LimitT,
} from '../../../typings/product-query.flow';

const cx = classNames.bind(cs);

const FREE_LISTINGS_COUNT = 200;

const getLinkText = (active, imported, name) => {
  let description = t(active ? 'link' : 'enable');

  if (imported) {
    description = t('imported');
  }

  return (
    <>
      <div>
        <b>{name}</b>
      </div>
      {description}
    </>
  );
};

type Props = {
  id: string,
  active: boolean,
  site: SiteT,
  name: string,
  anyImported: boolean,
  anySelectable: boolean,
  anySelected: boolean,
  registrationDate?: string,
  managedPayments: boolean,
  limits: LimitT[],
};

export default function ShowcaseLink({
  id,
  active,
  site,
  name,
  anyImported,
  anySelectable,
  anySelected,
  registrationDate,
  managedPayments,
  limits,
}: Props): React.Node {
  const [persistState, setPersistState] = usePersistState(keys.auFreeListings);
  const freeAUReleaseDate = new Date(2021, 8, 27, 12);
  const userRegistrationDate = registrationDate
    ? parseISO(registrationDate)
    : new Date();

  const showAuFreePromo =
    site.code === 'AU' &&
    managedPayments &&
    isBefore(userRegistrationDate, freeAUReleaseDate) &&
    (!active || !anySelected) &&
    anySelectable &&
    !anyImported &&
    persistState !== 'viewed';

  const siteLimit = limits.find((l) => l.site.id === site.id);
  const limitToShow = Math.min(
    pathOr<number, string, number, any>(0, ['limit'], siteLimit),
    FREE_LISTINGS_COUNT
  );

  const [activate] = useMutation(ActivateShowcase);

  const activateWithoutConfirm = () => {
    activate({
      variables: {
        input: {
          ids: [id],
        },
      },
    });
  };

  const { openLightbox } = useApolloLightbox('turnOnSite');

  const {
    recommendedSite,
    additionalSite,
    onNextOnboardingStep,
    onPrevOnboardingStep,
    isOnboardingStep,
    onboardingShown,
    continuedOnboardingStep,
    resetContinuedOnboardingStep,
    removeOnboardingStepsFromSkip,
  } = useOnboardingTour();

  const showTurnOnSite = () => {
    selectedShowcaseVar(id);
    if (!anySelectable) {
      activateWithoutConfirm();
      if (isOnboardingStep(2)) {
        onNextOnboardingStep();
      }
    } else {
      removeOnboardingStepsFromSkip([3]);
      openLightbox();
      if (isOnboardingStep(2)) {
        onNextOnboardingStep();
      }
    }
  };

  React.useEffect(() => {
    if (
      !active &&
      onboardingShown &&
      recommendedSite &&
      recommendedSite.code === site.code &&
      continuedOnboardingStep === 2
    ) {
      if (anySelectable) {
        removeOnboardingStepsFromSkip([3]);
        onPrevOnboardingStep();
        resetContinuedOnboardingStep();
        showTurnOnSite();
      } else {
        resetContinuedOnboardingStep();
        showTurnOnSite();
      }
    }
  }, [continuedOnboardingStep]);

  const Icon = (
    <FlagIcon
      name={site.code}
      disabled={!active}
      className={cx('flag', { active })}
    />
  );

  const renderContent = () => {
    if (active && !anySelectable) {
      return <div className={cs.link}>{Icon}</div>;
    }

    if (active) {
      return (
        <a href={settings.path.showcase(id)} className={cx('link', { active })}>
          {Icon}
        </a>
      );
    }

    return (
      <button type="button" className={cx('wrapper')} onClick={showTurnOnSite}>
        {Icon}
      </button>
    );
  };

  const contentWithTooltip = (
    <Tooltip
      text={getLinkText(active, anyImported, name)}
      className={cx('tooltip')}
    >
      {renderContent()}
    </Tooltip>
  );

  if (
    onboardingShown &&
    recommendedSite &&
    recommendedSite.code === site.code
  ) {
    return (
      <OnboardingHighlight round stepNumber={2}>
        {contentWithTooltip}
      </OnboardingHighlight>
    );
  }

  if (onboardingShown && additionalSite && additionalSite.code === site.code) {
    return (
      <OnboardingHighlight round stepNumber={7}>
        {contentWithTooltip}
      </OnboardingHighlight>
    );
  }

  if (!onboardingShown && showAuFreePromo && limitToShow) {
    return (
      <>
        <OnboardingHighlight round forceShow>
          {contentWithTooltip}
        </OnboardingHighlight>
        <AuFreePopup
          showcaseId={id}
          limit={limitToShow}
          onClose={setPersistState}
        />
      </>
    );
  }

  return contentWithTooltip;
}
